import React, { Fragment } from 'react';
import styled from 'styled-components';

import { Translation, useTranslation } from '~/components/Translation';
import {
	CTARow,
	FaqBlock,
	ImageBlock,
	Image640x560,
	MultipleSrcPicture,
	Signup,
	Header,
} from '~/components/Marketing';
import ResponsiveText from '~/components/ResponsiveText';
import Avatar from '~/components/Avatar';
import LinkBody from '~/components/LinkBodyV2';
import analytics from '~/services/analytics';
import usePageTrack from '~/services/analytics/usePageTrack';
import { media, visibility } from '~/styles/mixins';
import featureFlags from '~/constants/featureFlags';

import HeroImageLargePng from './hero_image_large.png';
import HeroImageLargeWebp from './hero_image_large.webp';
import HeroImageSmallPng from './hero_image_small.png';
import HeroImageSmallWebp from './hero_image_small.webp';

import BlockImageBeEverywherePng from './block_image_be_everywhere.png';
import BlockImageBeEverywhereWebp from './block_image_be_everywhere.webp';
import BlockImageMultiplePlatformsPng from './block_image_multiple_platforms.png';
import BlockImageMultiplePlatformsWebp from './block_image_multiple_platforms.webp';
import BlockImageStreamAnywherePng from './block_image_stream_anywhere.png';
import BlockImageStreamAnywhereWebp from './block_image_stream_anywhere.webp';

import MarcusQuoteAvatarSrc from './quote_avatar_marcus.png';
import MobileLandingSignup from '../../../Signup/scenes/MobileLandingSignup';

const HeroImageLarge = styled.div`
	width: 100%;
	max-width: 1200px;
	margin: 24px auto;
	${visibility.showMedium}
`;

const HeroImageLargeImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const HeroImageSmall = styled.div`
	width: 100%;
	max-width: 500px;
	margin: 16px auto;
	padding: 0 24px;
	${media.medium`
    padding: 0 80px;
  `}
	${visibility.hideMedium}
`;

const HeroImageSmallImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const faqList = [
	{
		question: <Translation tid="landingMultistreaming.faq.question.one" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingMultistreaming.faq.question.one.answer" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingMultistreaming.faq.question.two" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingMultistreaming.faq.question.two.answer" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingMultistreaming.faq.question.three" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingMultistreaming.faq.question.three.answer" />
				</ResponsiveText>
			</Fragment>
		),
	},
	{
		question: <Translation tid="landingMultistreaming.faq.question.four" />,
		answer: (
			<Fragment>
				<ResponsiveText variant="body">
					<Translation tid="landingMultistreaming.faq.question.four.answer" />
				</ResponsiveText>
			</Fragment>
		),
	},
];

const Multistreaming = () => {
	usePageTrack({
		name: analytics.events.MULTISTREAMING,
		category: analytics.events.category.MARKETING,
	});

	return (
		<main role="main">
			<Header
				title={<Translation tid="landingMultistreaming.title" />}
				subtitles={[
					<Translation key="1" tid="landingMultistreaming.subtitle" />,
				]}
				cta={<Signup />}
				redesign={featureFlags.orion.landingPageRedesignV2}
			/>
			<HeroImageLarge>
				<HeroImageLargeImage
					fallbackSrc={HeroImageLargePng}
					webpSrc={HeroImageLargeWebp}
					alt=""
				/>
			</HeroImageLarge>
			<HeroImageSmall>
				<HeroImageSmallImage
					fallbackSrc={HeroImageSmallPng}
					webpSrc={HeroImageSmallWebp}
					alt=""
				/>
			</HeroImageSmall>
			<MobileLandingSignup />
			<ImageBlock
				avatar={<Avatar src={MarcusQuoteAvatarSrc} alt="Marcus Sconiers" />}
				body={useTranslation('landingMultistreaming.block.beEverywhere.body')}
				header={useTranslation(
					'landingMultistreaming.block.beEverywhere.header',
				)}
				image={
					<Image640x560
						fallbackSrc={BlockImageBeEverywherePng}
						webpSrc={BlockImageBeEverywhereWebp}
						alt=""
					/>
				}
				name={useTranslation('landingMultistreaming.block.marcus.name')}
				quote={useTranslation('landingMultistreaming.block.marcus.quote')}
				role={useTranslation('landingMultistreaming.block.marcus.role')}
			/>
			<ImageBlock
				color="slate"
				header={useTranslation(
					'landingMultistreaming.block.multiplePlatforms.header',
				)}
				body={useTranslation(
					'landingMultistreaming.block.multiplePlatforms.body.one',
				)}
				bodyTwo={useTranslation(
					'landingMultistreaming.block.multiplePlatforms.body.two',
				)}
				image={
					<Image640x560
						fallbackSrc={BlockImageMultiplePlatformsPng}
						webpSrc={BlockImageMultiplePlatformsWebp}
						alt=""
					/>
				}
				reverse={true}
			/>
			<ImageBlock
				header={useTranslation(
					'landingMultistreaming.block.streamAnywhere.header',
				)}
				body={useTranslation('landingMultistreaming.block.streamAnywhere.body')}
				image={
					<Image640x560
						fallbackSrc={BlockImageStreamAnywherePng}
						webpSrc={BlockImageStreamAnywhereWebp}
						alt=""
					/>
				}
			/>
			<FaqBlock
				faqList={faqList}
				header={<Translation tid="landingMultistreaming.faq.header" />}
			/>
			<CTARow
				body={
					<Translation
						tid="landingMultistreaming.cta.body"
						components={{
							link: <LinkBody to="/contact-us" color="onPrimary" />,
						}}
					/>
				}
				cta={<Signup />}
				subtitle={<Translation tid="landingMultistreaming.cta.subtitle" />}
				title={<Translation tid="landingMultistreaming.cta.title" />}
			/>
		</main>
	);
};

export default Multistreaming;
